import { 
  AxisConfig, 
  LegendConfig, 
  TooltipConfig, 
  AnimationConfig,
  PaddingConfig,
  ThemeConfig,
} from '../types';
import { themes, UNISYS_FONT_FAMILY } from './colors';

// ==================== Default Values ====================
export const defaultPadding: Required<PaddingConfig> = {
  top: 30,
  right: 20,
  bottom: 50,
  left: 50,
};

// Default font family (Unisys with fallbacks)
export const defaultFontFamily = UNISYS_FONT_FAMILY;

export const defaultAxisConfig: AxisConfig = {
  display: true,
  grid: {
    display: true,
    color: 'rgba(0, 0, 0, 0.12)',
    lineWidth: 1,
  },
  ticks: {
    display: true,
    color: '#374151', // Gray-700: WCAG AAA compliant (10.69:1 on white)
    fontSize: 11,
    fontFamily: defaultFontFamily,
    padding: 8,
    maxTicksLimit: 8,
  },
  beginAtZero: true,
};

export const defaultLegendConfig: LegendConfig = {
  display: true,
  position: 'bottom',
  align: 'center',
  labels: {
    padding: 20,
    boxWidth: 12,
    boxHeight: 12,
    fontSize: 12,
    fontFamily: defaultFontFamily,
    color: '#374151', // Gray-700: WCAG AAA compliant
  },
};

export const defaultTooltipConfig: TooltipConfig = {
  enabled: true,
  backgroundColor: 'rgba(17, 24, 39, 0.95)', // Gray-900 with opacity for contrast
  textColor: '#ffffff',
  fontSize: 12,
  fontFamily: defaultFontFamily,
  cornerRadius: 4,
  padding: 10,
};

export const defaultAnimationConfig: AnimationConfig = {
  enabled: true,
  duration: 500,
  easing: 'ease-out',
  delay: 0,
  staggerDelay: 30,
};

// ==================== Merge Utilities ====================
export const mergePadding = (
  padding: PaddingConfig | number | undefined,
  defaults: Required<PaddingConfig> = defaultPadding
): Required<PaddingConfig> => {
  if (padding === undefined) return defaults;
  if (typeof padding === 'number') {
    return { top: padding, right: padding, bottom: padding, left: padding };
  }
  return {
    top: padding.top ?? defaults.top,
    right: padding.right ?? defaults.right,
    bottom: padding.bottom ?? defaults.bottom,
    left: padding.left ?? defaults.left,
  };
};

export const mergeAxisConfig = (
  config: AxisConfig | undefined,
  defaults: AxisConfig = defaultAxisConfig,
  theme?: ThemeConfig
): AxisConfig => {
  if (!config) return { ...defaults };
  return {
    display: config.display ?? defaults.display,
    title: config.title ? {
      display: config.title.display ?? false,
      text: config.title.text ?? '',
      color: config.title.color ?? theme?.textColor ?? '#666666',
      fontSize: config.title.fontSize ?? 12,
      fontWeight: config.title.fontWeight ?? 'normal',
      fontFamily: config.title.fontFamily ?? theme?.fontFamily,
      padding: config.title.padding ?? 10,
    } : undefined,
    grid: {
      display: config.grid?.display ?? defaults.grid?.display,
      color: config.grid?.color ?? theme?.gridColor ?? defaults.grid?.color,
      lineWidth: config.grid?.lineWidth ?? defaults.grid?.lineWidth,
      dashArray: config.grid?.dashArray,
    },
    ticks: {
      display: config.ticks?.display ?? defaults.ticks?.display,
      color: config.ticks?.color ?? theme?.textColor ?? defaults.ticks?.color,
      fontSize: config.ticks?.fontSize ?? theme?.fontSize ?? defaults.ticks?.fontSize,
      fontFamily: config.ticks?.fontFamily ?? theme?.fontFamily,
      padding: config.ticks?.padding ?? defaults.ticks?.padding,
      maxTicksLimit: config.ticks?.maxTicksLimit ?? defaults.ticks?.maxTicksLimit,
      stepSize: config.ticks?.stepSize,
      callback: config.ticks?.callback,
    },
    min: config.min,
    max: config.max,
    beginAtZero: config.beginAtZero ?? defaults.beginAtZero,
    position: config.position,
  };
};

export const mergeLegendConfig = (
  config: LegendConfig | undefined,
  defaults: LegendConfig = defaultLegendConfig,
  theme?: ThemeConfig
): LegendConfig => {
  if (!config) return { ...defaults };
  return {
    display: config.display ?? defaults.display,
    position: config.position ?? defaults.position,
    align: config.align ?? defaults.align,
    labels: {
      color: config.labels?.color ?? theme?.textColor ?? defaults.labels?.color,
      fontSize: config.labels?.fontSize ?? theme?.fontSize ?? defaults.labels?.fontSize,
      fontFamily: config.labels?.fontFamily ?? theme?.fontFamily,
      padding: config.labels?.padding ?? defaults.labels?.padding,
      boxWidth: config.labels?.boxWidth ?? defaults.labels?.boxWidth,
      boxHeight: config.labels?.boxHeight ?? defaults.labels?.boxHeight,
      usePointStyle: config.labels?.usePointStyle ?? defaults.labels?.usePointStyle,
    },
    onClick: config.onClick,
  };
};

export const mergeTooltipConfig = (
  config: TooltipConfig | undefined,
  defaults: TooltipConfig = defaultTooltipConfig,
  theme?: ThemeConfig
): TooltipConfig => {
  if (!config) return { ...defaults };
  return {
    enabled: config.enabled ?? defaults.enabled,
    backgroundColor: config.backgroundColor ?? theme?.tooltipBackground ?? defaults.backgroundColor,
    textColor: config.textColor ?? theme?.tooltipTextColor ?? defaults.textColor,
    fontSize: config.fontSize ?? theme?.fontSize ?? defaults.fontSize,
    fontFamily: config.fontFamily ?? theme?.fontFamily,
    borderColor: config.borderColor,
    borderWidth: config.borderWidth,
    cornerRadius: config.cornerRadius ?? defaults.cornerRadius,
    padding: config.padding ?? defaults.padding,
    formatter: config.formatter,
  };
};

export const mergeAnimationConfig = (
  config: AnimationConfig | boolean | undefined,
  defaults: AnimationConfig = defaultAnimationConfig
): AnimationConfig => {
  if (config === false) {
    return { ...defaults, enabled: false, duration: 0 };
  }
  if (config === true || config === undefined) {
    return { ...defaults };
  }
  return {
    enabled: config.enabled ?? defaults.enabled,
    duration: config.duration ?? defaults.duration,
    easing: config.easing ?? defaults.easing,
    delay: config.delay ?? defaults.delay,
    staggerDelay: config.staggerDelay ?? defaults.staggerDelay,
  };
};

/**
 * Get default theme or custom theme
 */
export const getActiveTheme = (
  themeName: string = 'default',
  customTheme?: ThemeConfig
): ThemeConfig => {
  if (customTheme) {
    return {
      ...themes.default,
      ...customTheme,
    };
  }
  return themes[themeName as keyof typeof themes] || themes.default;
};
